import React from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Collapse,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RoomIcon from "@mui/icons-material/Room";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CopyTextButton from "../../../utils/CopyTextButton";

const RentalSettingsSummary = ({
  canEdit = true,
  includeAddress,
  addressLines,
  rentalPeriods,
  hasVacantPeriod,
  vacantPeriods,
  showAddress,
  setShowAddress,
  onEditSettings,
}) => {
  const theme = useTheme();
  const isAddressProvided = includeAddress && addressLines.length > 0;
  const isRentalProvided = rentalPeriods && rentalPeriods.length > 0;
  const isVacantProvided =
    hasVacantPeriod && vacantPeriods && vacantPeriods.length > 0;

  const getAddressSummary = () =>
    isAddressProvided ? "Provided" : "Not Provided";
  const getRentalSummary = () => {
    if (!isRentalProvided) return "Not Provided";
    if (rentalPeriods.length === 1) return "Provided";
    return `Provided (${rentalPeriods.length} periods)`;
  };
  const getVacantSummary = () => {
    if (!isVacantProvided) return "Not Provided";
    if (vacantPeriods.length === 1) return "Provided";
    return `Provided (${vacantPeriods.length} periods)`;
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          sx={{ flexWrap: "wrap" }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              flexShrink: 0,
            }}
          >
            <HomeWorkOutlinedIcon
              sx={{
                fontSize: 24,
                color: "primary.main",
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant='h6'
              sx={{
                fontWeight: 600,
                color: "text.primary",
                mb: 0.5,
              }}
            >
              Rental Information
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: "text.secondary",
                fontSize: "0.85rem",
              }}
            >
              View and manage your rental property information
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "center",
                mt: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: isAddressProvided
                    ? alpha(theme.palette.success.main, 0.12)
                    : alpha(theme.palette.grey[400], 0.12),
                  color: isAddressProvided
                    ? theme.palette.success.main
                    : "text.secondary",
                  fontSize: "0.75rem",
                  fontWeight: isAddressProvided ? 600 : 500,
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <RoomIcon sx={{ fontSize: "0.875rem" }} />
                Address: {getAddressSummary()}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: isRentalProvided
                    ? alpha(theme.palette.info.main, 0.12)
                    : alpha(theme.palette.grey[400], 0.12),
                  color: isRentalProvided
                    ? theme.palette.info.main
                    : "text.secondary",
                  fontSize: "0.75rem",
                  fontWeight: isRentalProvided ? 600 : 500,
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <HomeWorkOutlinedIcon sx={{ fontSize: "0.875rem" }} />
                Rental: {getRentalSummary()}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: isVacantProvided
                    ? alpha(theme.palette.warning.main, 0.12)
                    : alpha(theme.palette.grey[400], 0.12),
                  color: isVacantProvided
                    ? theme.palette.warning.main
                    : "text.secondary",
                  fontSize: "0.75rem",
                  fontWeight: isVacantProvided ? 600 : 500,
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <EventBusyIcon sx={{ fontSize: "0.875rem" }} />
                Vacant: {getVacantSummary()}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Button
              size='small'
              variant='outlined'
              startIcon={
                <VisibilityIcon
                  sx={{
                    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    transform: showAddress ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              }
              onClick={() => setShowAddress((prev) => !prev)}
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 500,
                px: 1.5,
                py: 0.5,
                fontSize: "0.875rem",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              {showAddress ? "Hide Details" : "Show Details"}
            </Button>
            {canEdit && (
              <Button
                size='small'
                variant='outlined'
                onClick={onEditSettings}
                startIcon={<HomeWorkOutlinedIcon />}
                sx={{
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 500,
                  px: 1.5,
                  py: 0.5,
                  fontSize: "0.875rem",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Edit Settings
              </Button>
            )}
          </Box>
        </Stack>
      </Paper>

      <Collapse
        in={showAddress}
        timeout={400}
        easing={{
          enter: "cubic-bezier(0.4, 0, 0.2, 1)",
          exit: "cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        sx={{
          "& .MuiCollapse-wrapper": {
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 2,
            mt: -3,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            background: `linear-gradient(145deg, ${
              theme.palette.background.paper
            }, ${alpha(theme.palette.primary.main, 0.01)})`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              flexWrap: "wrap",
              animation: showAddress
                ? "slideInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                : "none",
              "@keyframes slideInUp": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(20px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            }}
          >
            {/* Address Card */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                bgcolor: "background.paper",
                minWidth: 280,
                maxWidth: 380,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  transform: "translateY(-2px)",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1.5,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  flexShrink: 0,
                  mt: 0.5,
                }}
              >
                <RoomIcon
                  sx={{
                    fontSize: "1.25rem",
                    color: "primary.main",
                    transition: "all 0.2s ease",
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    mb: 1,
                  }}
                >
                  Property Address
                </Typography>
                {includeAddress && addressLines.length > 0 ? (
                  <Box>
                    {addressLines.map((line, idx) => (
                      <Typography
                        key={idx}
                        variant='body2'
                        sx={{
                          wordBreak: "break-word",
                          mb: idx < addressLines.length - 1 ? 0.5 : 0,
                          color: "text.primary",
                          transition: "color 0.2s ease",
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant='body2'
                    sx={{
                      color: "text.disabled",
                      fontStyle: "italic",
                    }}
                  >
                    No address provided.
                  </Typography>
                )}
              </Box>
              {includeAddress && addressLines.length > 0 && (
                <CopyTextButton
                  text={addressLines.join(", ")}
                  tooltipText='Copy Address'
                  copiedTooltipText='Copied!'
                  sx={{ mt: 0.5 }}
                  size='small'
                />
              )}
            </Paper>

            {/* Rental Period Card */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                bgcolor: "background.paper",
                minWidth: 280,
                maxWidth: 380,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  transform: "translateY(-2px)",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1.5,
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                  flexShrink: 0,
                  mt: 0.5,
                }}
              >
                <HomeWorkOutlinedIcon
                  sx={{
                    fontSize: "1.25rem",
                    color: "info.main",
                    transition: "all 0.2s ease",
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    mb: 1,
                  }}
                >
                  Rental Periods
                </Typography>
                {rentalPeriods.length > 0 ? (
                  <Box>
                    {rentalPeriods.map((period, index) => (
                      <Typography
                        key={index}
                        variant='body2'
                        sx={{
                          mb: index < rentalPeriods.length - 1 ? 0.5 : 0,
                          color: "text.primary",
                          transition: "color 0.2s ease",
                        }}
                      >
                        {period.startDate} to {period.endDate}
                        {period.days ? ` (${period.days} days)` : ""}
                      </Typography>
                    ))}
                    {rentalPeriods.length > 1 && (
                      <Typography
                        variant='caption'
                        sx={{
                          color: "text.secondary",
                          fontWeight: 500,
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        Total: {rentalPeriods.length} periods
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography
                    variant='body2'
                    sx={{
                      color: "text.disabled",
                      fontStyle: "italic",
                    }}
                  >
                    Not set
                  </Typography>
                )}
              </Box>
              {rentalPeriods.length > 0 && (
                <CopyTextButton
                  text={rentalPeriods
                    .map(
                      (period) =>
                        `${period.startDate} to ${period.endDate}${
                          period.days ? ` (${period.days} days)` : ""
                        }`
                    )
                    .join(", ")}
                  tooltipText='Copy Period'
                  copiedTooltipText='Copied!'
                  sx={{ mt: 0.5 }}
                  size='small'
                />
              )}
            </Paper>

            {/* Vacant Period Card */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                bgcolor: "background.paper",
                minWidth: 280,
                maxWidth: 380,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  transform: "translateY(-2px)",
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1.5,
                  bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                  flexShrink: 0,
                  mt: 0.5,
                }}
              >
                <EventBusyIcon
                  sx={{
                    fontSize: "1.25rem",
                    color: "warning.main",
                    transition: "all 0.2s ease",
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    mb: 1,
                  }}
                >
                  Vacant Periods
                </Typography>
                {vacantPeriods.length > 0 ? (
                  <Box>
                    {vacantPeriods.map((period, index) => (
                      <Typography
                        key={index}
                        variant='body2'
                        sx={{
                          mb: index < vacantPeriods.length - 1 ? 0.5 : 0,
                          color: "text.primary",
                          transition: "color 0.2s ease",
                        }}
                      >
                        {period.startDate} to {period.endDate}
                        {period.days ? ` (${period.days} days)` : ""}
                      </Typography>
                    ))}
                    {vacantPeriods.length > 1 && (
                      <Typography
                        variant='caption'
                        sx={{
                          color: "text.secondary",
                          fontWeight: 500,
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        Total: {vacantPeriods.length} periods
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography
                    variant='body2'
                    sx={{
                      color: "text.disabled",
                      fontStyle: "italic",
                    }}
                  >
                    None
                  </Typography>
                )}
              </Box>
              {vacantPeriods.length > 0 && (
                <CopyTextButton
                  text={vacantPeriods
                    .map(
                      (period) =>
                        `${period.startDate} to ${period.endDate}${
                          period.days ? ` (${period.days} days)` : ""
                        }`
                    )
                    .join(", ")}
                  tooltipText='Copy Period'
                  copiedTooltipText='Copied!'
                  sx={{ mt: 0.5 }}
                  size='small'
                />
              )}
            </Paper>
          </Box>
        </Paper>
      </Collapse>
    </>
  );
};

export default RentalSettingsSummary;
